import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GppBadIcon from '@mui/icons-material/GppBad';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import {
  CANONICAL_LEVELS,
  canonicalLevelRank,
  certificateBadge,
  certificateBadgeLabelKey,
  levelLabelKey,
  type CanonicalLevel,
  type CertificateStatus,
} from '@/utils/skillLevels';

export interface MatrixCellProps {
  userId: string;
  skillId: string;
  userName: string;
  skillName: string;
  level: CanonicalLevel | null;
  wantsToLearn: boolean;
  certificateStatus: CertificateStatus | undefined;
  requiresValidCertificate: boolean;
  canEdit: boolean;
  highlighted: boolean;
  lacking: boolean;
  onOpen: (userId: string, skillId: string, anchor: HTMLElement) => void;
  onSetLevel: (userId: string, skillId: string, level: CanonicalLevel) => void;
}

function MatrixCellInner({
  userId,
  skillId,
  userName,
  skillName,
  level,
  wantsToLearn,
  certificateStatus,
  requiresValidCertificate,
  canEdit,
  highlighted,
  lacking,
  onOpen,
  onSetLevel,
}: MatrixCellProps) {
  const { t } = useTranslation('hr');
  const { t: ts } = useTranslation('settings');
  const rank = canonicalLevelRank(level);
  const badge = certificateBadge({
    status: certificateStatus,
    requiresValidCertificate,
  });

  const aria = [
    t('skillsMatrix.cellAria', { user: userName, skill: skillName }),
    ts(levelLabelKey(level)),
    wantsToLearn ? ts('skills.levels.wantsToLearn') : null,
    badge === 'none' ? null : ts(certificateBadgeLabelKey(badge)),
    lacking ? t('skillsMatrix.lackingRequired') : null,
  ]
    .filter(Boolean)
    .join('. ');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!canEdit) return;
    if (event.key >= '1' && event.key <= '4') {
      const next = CANONICAL_LEVELS[Number(event.key) - 1];
      if (next) {
        event.preventDefault();
        onSetLevel(userId, skillId, next);
      }
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onOpen(userId, skillId, event.currentTarget);
    }
  };

  return (
    <Box
      component="button"
      type="button"
      data-user-id={userId}
      data-skill-id={skillId}
      data-highlighted={highlighted ? 'true' : 'false'}
      data-lacking={lacking ? 'true' : 'false'}
      aria-label={aria}
      disabled={!canEdit}
      onClick={(event) => {
        if (canEdit) onOpen(userId, skillId, event.currentTarget);
      }}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        width: '100%',
        minHeight: 44,
        px: 0.5,
        border: 1,
        borderColor: highlighted ? 'primary.main' : 'transparent',
        bgcolor: lacking ? 'warning.light' : highlighted ? 'action.selected' : 'transparent',
        cursor: canEdit ? 'pointer' : 'default',
        borderRadius: 1,
      }}
    >
      {CANONICAL_LEVELS.map((value, index) => {
        const filled = rank > index;
        const Icon = filled ? CircleIcon : CircleOutlinedIcon;
        return (
          <Icon
            key={value}
            sx={{ fontSize: 10, color: filled ? 'primary.main' : 'action.disabled' }}
            aria-hidden
          />
        );
      })}
      {wantsToLearn && (
        <Tooltip title={ts('skills.levels.wantsToLearn')}>
          <Box component="span" sx={{ display: 'inline-flex' }} aria-label={ts('skills.levels.wantsToLearn')}>
            <SchoolIcon sx={{ fontSize: 14, color: 'info.main', ml: 0.25 }} aria-hidden />
          </Box>
        </Tooltip>
      )}
      {badge === 'valid' && (
        <Tooltip title={ts(certificateBadgeLabelKey(badge))}>
          <Box component="span" sx={{ display: 'inline-flex' }} aria-label={ts(certificateBadgeLabelKey(badge))}>
            <VerifiedIcon sx={{ fontSize: 14, color: 'success.main' }} aria-hidden />
          </Box>
        </Tooltip>
      )}
      {badge === 'expiring' && (
        <Tooltip title={ts(certificateBadgeLabelKey(badge))}>
          <Box component="span" sx={{ display: 'inline-flex' }} aria-label={ts(certificateBadgeLabelKey(badge))}>
            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} aria-hidden />
          </Box>
        </Tooltip>
      )}
      {badge === 'expired' && (
        <Tooltip title={ts(certificateBadgeLabelKey(badge))}>
          <Box component="span" sx={{ display: 'inline-flex' }} aria-label={ts(certificateBadgeLabelKey(badge))}>
            <GppBadIcon sx={{ fontSize: 14, color: 'error.main' }} aria-hidden />
          </Box>
        </Tooltip>
      )}
      {badge === 'missing' && (
        <Tooltip title={ts(certificateBadgeLabelKey(badge))}>
          <Box component="span" sx={{ display: 'inline-flex' }} aria-label={ts(certificateBadgeLabelKey(badge))}>
            <ReportGmailerrorredIcon sx={{ fontSize: 14, color: 'error.main' }} aria-hidden />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

function cellEqual(prev: MatrixCellProps, next: MatrixCellProps): boolean {
  return (
    prev.userId === next.userId &&
    prev.skillId === next.skillId &&
    prev.userName === next.userName &&
    prev.skillName === next.skillName &&
    prev.level === next.level &&
    prev.wantsToLearn === next.wantsToLearn &&
    prev.certificateStatus === next.certificateStatus &&
    prev.requiresValidCertificate === next.requiresValidCertificate &&
    prev.canEdit === next.canEdit &&
    prev.highlighted === next.highlighted &&
    prev.lacking === next.lacking &&
    prev.onOpen === next.onOpen &&
    prev.onSetLevel === next.onSetLevel
  );
}

export const MatrixCell = memo(MatrixCellInner, cellEqual);
